import "client-only";

import {
  StrategyDetailsListSchema,
  StrategyDetailsSchema,
  StrategySettingsSchema,
  type StrategyRecord,
  type StrategySettingsContract,
  type StrategyWrite,
} from "@/contracts/strategies";
import { client } from "@/lib/platform/client";

function strategyPath(strategyId: string): string {
  return `/api/strategies/${encodeURIComponent(strategyId)}`;
}

async function fetchSettings(strategyId: string): Promise<StrategySettingsContract> {
  return client.get(`${strategyPath(strategyId)}/settings`, {
    expect: StrategySettingsSchema,
  });
}

async function create(write: StrategyWrite): Promise<StrategyRecord> {
  const details = await client.post("/api/strategies", write.details, {
    expect: StrategyDetailsSchema,
  });
  const settings = await client.put(
    `${strategyPath(details.id)}/settings`,
    write.settings,
    { expect: StrategySettingsSchema },
  );

  return { details, settings };
}

async function fetchStrategies() {
  return client.get("/api/strategies", {
    expect: StrategyDetailsListSchema,
  });
}

async function update(strategyId: string, write: StrategyWrite): Promise<StrategyRecord> {
  const details = await client.put(
    strategyPath(strategyId),
    write.details,
    { expect: StrategyDetailsSchema },
  );
  const settings = await client.put(
    `${strategyPath(strategyId)}/settings`,
    write.settings,
    { expect: StrategySettingsSchema },
  );

  return { details, settings };
}

async function deleteStrategy(strategyId: string): Promise<void> {
  await client.delete(strategyPath(strategyId));
}

function buildThumbnailUrl(strategyId: string, revision?: number): string {
  const path = `${strategyPath(strategyId)}/thumbnail`;
  return revision ? `${path}?revision=${revision}` : path;
}

async function uploadThumbnail(strategyId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append("image", file);
  await client.put(buildThumbnailUrl(strategyId), formData);
}

async function deleteThumbnail(strategyId: string): Promise<void> {
  await client.delete(buildThumbnailUrl(strategyId));
}

export const strategies = {
  buildThumbnailUrl,
  create,
  delete: deleteStrategy,
  deleteThumbnail,
  fetchSettings,
  fetchStrategies,
  update,
  uploadThumbnail,
};
